import { makeObservable, observable, action, runInAction, toJS } from 'mobx';
import StoreUtils from './store-utils';
import UserApi from '../rest-api/user-api';

const makeDexieSafe = (value) => JSON.parse(JSON.stringify(toJS(value)));

class SavedHandoutStore {
    savedHandoutData = {};
    db;
    idToken;
    isInitialized = false;

    constructor(userId, idToken) {
        this.db = StoreUtils.createDexieUserDB(userId, 'SavedHandoutStore');
        this.idToken = idToken;

        this.db.version(1).stores({
            savedHandouts:
                'savedHandoutId, userId, baseContentId, baseContentIdUserIdCompositeKey, baseLastUpdated, savedStatus, lastUpdated, updatedBy, updatedById, notes, patientId',
        });

        makeObservable(this, {
            savedHandoutData: observable,
            setSavedHandoutData: action,
            addOrUpdateSavedHandout: action,
            removeSavedHandout: action,
            isInitialized: observable,
        });
    }

    async initialize(userId) {
        const storedHandouts = await this.db.savedHandouts.toArray();

        runInAction(() => {
            for (const handout of storedHandouts) {
                this.savedHandoutData[handout.savedHandoutId] = handout;
            }
        });

        try {
            await this.fetchSavedHandoutsFromBackend(userId);
        } catch (err) {
            console.error('SavedHandoutStore initialization fetch error:', err);
        }

        runInAction(() => {
            this.isInitialized = true;
        });
    }

    async fetchSavedHandoutsFromBackend(userId) {
        try {
            const result = await UserApi.getSavedHandouts(this.idToken);
            if (result && Array.isArray(result.savedHandouts)) {
                await this.setSavedHandoutData(result.savedHandouts);
            }
        } catch (error) {
            console.error('Error fetching saved handouts from backend:', error);
        }
    }

    async setSavedHandoutData(arrayOfHandouts) {
        const safeArray = Array.isArray(arrayOfHandouts)
            ? arrayOfHandouts.map((handout) => makeDexieSafe(handout))
            : [];

        try {
            await this.db.savedHandouts.clear();
            if (safeArray.length > 0) {
                await this.db.savedHandouts.bulkPut(safeArray);
            }
        } catch (error) {
            console.error('SavedHandoutStore Dexie sync error:', error);
        }

        runInAction(() => {
            this.savedHandoutData = {};
            safeArray.forEach((handout) => {
                this.savedHandoutData[handout.savedHandoutId] = handout;
            });
        });
    }

    async addOrUpdateSavedHandout(handout) {
        if (!handout || !handout.savedHandoutId) {
            console.error('Invalid saved handout data:', handout);
            return;
        }

        const plainHandout = makeDexieSafe(handout);

        try {
            await this.db.savedHandouts.put(plainHandout);
        } catch (error) {
            console.error('SavedHandoutStore Dexie put error:', error);
        }

        runInAction(() => {
            this.savedHandoutData[plainHandout.savedHandoutId] = plainHandout;
        });
    }

    async getSavedHandoutById(savedHandoutId) {
        if (this.savedHandoutData[savedHandoutId]) {
            return this.savedHandoutData[savedHandoutId];
        }

        return await this.db.savedHandouts.get(savedHandoutId);
    }

    async removeSavedHandout(savedHandoutId) {
        if (!savedHandoutId) return;
        try {
            await this.db.savedHandouts.delete(savedHandoutId);
        } catch (error) {
            console.error('SavedHandoutStore Dexie delete error:', error);
        }
        runInAction(() => {
            delete this.savedHandoutData[savedHandoutId];
        });
    }

    async deleteDatabase() {
        await this.db.delete();
    }
}

export default async function createSavedHandoutStore(userId, idToken) {
    const store = new SavedHandoutStore(userId, idToken);
    await store.initialize(userId);
    return store;
}
